/**
 * FUEC Module API Service
 * Servicio para comunicación con endpoints FUEC del backend
 */

import api from "../../../axiosConfig";

// ============================================
// FUEC - Operaciones principales
// ============================================

/**
 * Obtener listado de FUECs con filtros
 */
export const fetchFuecs = async (params = {}) => {
  const response = await api.get("/fuec", { params });
  return response.data;
};

/**
 * Obtener un FUEC por ID
 */
export const fetchFuecById = async (id) => {
  const response = await api.get(`/fuec/${id}`);
  return response.data;
};

/**
 * Generar un nuevo FUEC
 */
export const generateFuec = async (data) => {
  const response = await api.post("/fuec", data);
  return response.data;
};

/**
 * Buscar FUEC reutilizable para un traslado
 */
export const buscarFuecReutilizable = async (data) => {
  const response = await api.post("/fuec/buscar-reutilizable", data);
  return response.data;
};

/**
 * Calcular vigencia automática del FUEC basada en vencimientos de documentos
 * Según documentación sección 1.15: La fecha fin es la más próxima de vencimiento
 * entre los documentos del vehículo y licencia del conductor
 */
export const calcularVigenciaFuec = async (vehiculoId, conductorId, fechaInicio = null) => {
  const response = await api.post("/fuec/calcular-vigencia", {
    vehiculo_id: vehiculoId,
    conductor_id: conductorId,
    fecha_inicio: fechaInicio,
  });
  return response.data;
};

/**
 * Asociar FUEC existente a un traslado
 */
export const asociarFuec = async (fuecId, trasladoId) => {
  const response = await api.post(`/fuec/${fuecId}/asociar-traslado`, { traslado_id: trasladoId });
  return response.data;
};

/**
 * Anular un FUEC
 */
export const anularFuec = async (id, motivo) => {
  const response = await api.post(`/fuec/${id}/anular`, { motivo });
  return response.data;
};

/**
 * Descargar PDF de un FUEC individual
 */
export const downloadFuecPdf = async (id) => {
  const response = await api.get(`/fuec/${id}/pdf`, {
    responseType: "blob",
  });
  return response.data;
};

/**
 * Descargar PDF de FUECs unificados
 */
export const downloadFuecsPdfUnificado = async (ids) => {
  const response = await api.post(
    "/fuec/pdf-unificado",
    { fuec_ids: ids },
    { responseType: "blob" }
  );
  return response.data;
};

/**
 * Obtener estadisticas de FUECs
 */
export const fetchFuecStats = async (params = {}) => {
  const response = await api.get("/fuec/estadisticas", { params });
  return response.data;
};

/**
 * Obtener FUECs de un cliente
 */
export const fetchFuecsByCliente = async (clienteId, params = {}) => {
  const response = await api.get("/fuec", { params: { ...params, cliente_id: clienteId } });
  return response.data;
};

/**
 * Obtener FUECs vigentes de un vehiculo
 */
export const fetchFuecsByVehiculo = async (vehiculoId) => {
  const response = await api.get(`/fuec/vehiculo/${vehiculoId}/vigentes`);
  return response.data;
};

/**
 * Obtener FUECs vigentes
 */
export const fetchFuecsVigentes = async () => {
  const response = await api.get("/fuec", { params: { vigente: true } });
  return response.data;
};

// ============================================
// Objetos de Contrato
// ============================================

/**
 * Obtener lista de objetos de contrato activos
 */
export const fetchObjetosContrato = async (params = {}) => {
  const response = await api.get("/objetos-contrato", { params });
  return response.data;
};

/**
 * Obtener lista simplificada para selector
 */
export const fetchObjetosContratoList = async () => {
  const response = await api.get("/objetos-contrato/list");
  return response.data;
};

/**
 * Crear objeto de contrato
 */
export const createObjetoContrato = async (data) => {
  const response = await api.post("/objetos-contrato", data);
  return response.data;
};

/**
 * Actualizar objeto de contrato
 */
export const updateObjetoContrato = async (id, data) => {
  const response = await api.put(`/objetos-contrato/${id}`, data);
  return response.data;
};

/**
 * Toggle activo/inactivo de objeto de contrato
 */
export const toggleObjetoContrato = async (id) => {
  const response = await api.put(`/objetos-contrato/${id}/toggle-activo`);
  return response.data;
};

/**
 * Eliminar objeto de contrato
 */
export const deleteObjetoContrato = async (id) => {
  const response = await api.delete(`/objetos-contrato/${id}`);
  return response.data;
};

// ============================================
// Configuracion FUEC
// ============================================

/**
 * Obtener todas las configuraciones como objeto key-value
 */
export const fetchFuecConfig = async () => {
  const response = await api.get("/fuec-config/all");
  return response.data;
};

/**
 * Obtener configuraciones como lista
 */
export const fetchFuecConfigList = async () => {
  const response = await api.get("/fuec-config");
  return response.data;
};

/**
 * Obtener datos de empresa para FUEC
 */
export const fetchDatosEmpresa = async () => {
  const response = await api.get("/fuec-config/empresa");
  return response.data;
};

/**
 * Actualizar una configuracion
 */
export const updateFuecConfig = async (key, value, descripcion = null) => {
  const response = await api.put(`/fuec-config/${key}`, { value, descripcion });
  return response.data;
};

/**
 * Actualizar multiples configuraciones
 */
export const updateFuecConfigBulk = async (configs) => {
  const response = await api.put("/fuec-config", { configs });
  return response.data;
};

/**
 * Limpiar cache de configuraciones
 */
export const clearFuecConfigCache = async () => {
  const response = await api.post("/fuec-config/clear-cache");
  return response.data;
};

/**
 * Subir logo (empresa o ministerio)
 * @param {File} file - Archivo de imagen
 * @param {string} tipo - 'empresa' o 'ministerio'
 */
export const uploadFuecLogo = async (file, tipo) => {
  const formData = new FormData();
  formData.append('logo', file);
  formData.append('tipo', tipo);

  const response = await api.post('/fuec-config/upload-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

/**
 * Obtener logos actuales
 */
export const getFuecLogos = async () => {
  const response = await api.get('/fuec-config/logos');
  return response.data;
};

// ============================================
// Helpers para datos de formularios
// ============================================

/**
 * Obtener clientes/EPS activos para FUEC
 */
export const fetchClientes = async () => {
  const response = await api.get("/clientes");
  return response.data;
};

/**
 * Obtener vehiculos activos
 */
export const fetchVehiculos = async () => {
  const response = await api.get("/vehiculos");
  return response.data;
};

/**
 * Obtener conductores activos
 */
export const fetchConductores = async () => {
  const response = await api.get("/conductores");
  return response.data;
};

/**
 * Obtener ciudades por departamento
 */
export const fetchCiudadesByDepartamento = async (departamentoId) => {
  const response = await api.get(`/departamentos/${departamentoId}/ciudades`);
  return response.data;
};

/**
 * Obtener departamentos
 */
export const fetchDepartamentos = async () => {
  const response = await api.get("/departamentos");
  return response.data;
};

/**
 * Obtener traslados pendientes de FUEC
 */
export const fetchTrasladosPendientesFuec = async () => {
  const response = await api.get("/traslados", {
    params: {
      sin_fuec: true,
      estado: "programado",
    },
  });
  return response.data;
};

const fuecApi = {
  // FUEC
  fetchFuecs,
  fetchFuecById,
  generateFuec,
  buscarFuecReutilizable,
  calcularVigenciaFuec,
  asociarFuec,
  anularFuec,
  downloadFuecPdf,
  downloadFuecsPdfUnificado,
  fetchFuecStats,
  fetchFuecsByCliente,
  fetchFuecsByVehiculo,
  fetchFuecsVigentes,
  // Objetos Contrato
  fetchObjetosContrato,
  fetchObjetosContratoList,
  createObjetoContrato,
  updateObjetoContrato,
  toggleObjetoContrato,
  deleteObjetoContrato,
  // Config
  fetchFuecConfig,
  fetchFuecConfigList,
  fetchDatosEmpresa,
  updateFuecConfig,
  updateFuecConfigBulk,
  clearFuecConfigCache,
  uploadFuecLogo,
  getFuecLogos,
  // Helpers
  fetchClientes,
  fetchVehiculos,
  fetchConductores,
  fetchCiudadesByDepartamento,
  fetchDepartamentos,
  fetchTrasladosPendientesFuec,
};

export default fuecApi;
